import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { EmojiEvents, SportsKabaddi } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import {
  hasCommunityWins,
  hasTriviaMedals,
  hasTriviaPlaque,
} from "../../utils/fanHubProfile";
import { getSportPath } from "../../utils/sportWorkspace";
import { brandOutlinedButtonSx, brandRgba } from "../../utils/brandAssets";
import { horizontalScroll } from "../home/homeStyles";
import {
  fanHubOverlineSx,
  fanHubPaperSx,
  sportAccent,
} from "./fanHubStyles";

const GOLD = "#E8C36A";

const Shelf = ({ overline, title, children, action }) => (
  <Box sx={{ ...fanHubPaperSx(GOLD), p: 2.25, flex: 1, minWidth: 0 }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 1,
        mb: 1.5,
      }}
    >
      <Box>
        <Typography variant="overline" sx={fanHubOverlineSx}>
          {overline}
        </Typography>
        <Typography variant="h6" fontWeight={800} sx={{ mt: 0.25 }}>
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
    {children}
  </Box>
);

const Invite = ({ copy }) => (
  <Typography color="text.secondary" variant="body2">
    {copy}
  </Typography>
);

const PlaqueStat = ({ label, value }) => (
  <Box sx={{ minWidth: 72 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={800} sx={{ color: GOLD }}>
      {value}
    </Typography>
  </Box>
);

const Medal = ({ medal }) => (
  <Box
    sx={{
      flex: "0 0 auto",
      width: 92,
      textAlign: "center",
    }}
  >
    <Box
      sx={{
        width: 56,
        height: 56,
        mx: "auto",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `radial-gradient(circle at 30% 25%, #fff3c4 0%, ${GOLD} 42%, #8a6a1a 100%)`,
        boxShadow: `0 0 18px ${brandRgba(GOLD, 0.35)}`,
        color: "#2a2108",
      }}
    >
      <EmojiEvents sx={{ fontSize: 28 }} />
    </Box>
    <Typography
      variant="caption"
      fontWeight={700}
      sx={{ display: "block", mt: 1 }}
      noWrap
    >
      {medal.name}
    </Typography>
  </Box>
);

const FanHubTrophyRoom = ({ trophyRoom, preferredSport }) => {
  const trivia = trophyRoom?.trivia || {};
  const community = trophyRoom?.community || {};
  const showPlaque = hasTriviaPlaque(trivia.plaque);
  const showMedals = hasTriviaMedals(trivia.medals);
  const showRecord = hasCommunityWins(community.record);
  const showcase = community.showcase;
  const communityHref = getSportPath(preferredSport || "mlb", "community");
  const accent = sportAccent(preferredSport);

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="overline" sx={fanHubOverlineSx}>
        Hall
      </Typography>
      <Typography variant="h5" fontWeight={800} sx={{ mt: 0.25, mb: 2 }}>
        Trophy room
      </Typography>
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        <Shelf
          overline="Trivia"
          title="Medals"
          action={
            <Button
              component={RouterLink}
              to={trivia.href || "/app/trivia"}
              size="small"
              variant="outlined"
              sx={brandOutlinedButtonSx}
            >
              {showMedals || showPlaque ? "Cabinet" : "Play"}
            </Button>
          }
        >
          {showPlaque && (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: showMedals ? 2 : 0 }}>
              <PlaqueStat label="Level" value={trivia.plaque.level} />
              <PlaqueStat
                label="Streak"
                value={trivia.plaque.currentStreak}
              />
              <PlaqueStat
                label="Accuracy"
                value={`${Math.round((trivia.plaque.overallAccuracy || 0) * 100)}%`}
              />
            </Box>
          )}
          {showMedals ? (
            <Box>
              {trivia.unlockedCount > trivia.medals.length && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Latest {trivia.medals.length} of {trivia.unlockedCount}
                </Typography>
              )}
              <Box sx={horizontalScroll}>
                {trivia.medals.map((medal) => (
                  <Medal key={medal.id || medal.name} medal={medal} />
                ))}
              </Box>
            </Box>
          ) : (
            <Invite copy="Play trivia to earn medals." />
          )}
        </Shelf>

        <Shelf
          overline="Community"
          title="Wins"
          action={
            <Button
              component={RouterLink}
              to={communityHref}
              size="small"
              variant="outlined"
              sx={brandOutlinedButtonSx}
            >
              Battles
            </Button>
          }
        >
          {showRecord ? (
            <Box sx={{ display: "flex", gap: 2, mb: showcase ? 2 : 0 }}>
              <PlaqueStat
                label="Record"
                value={`${community.record.wins}–${community.record.losses}`}
              />
              <PlaqueStat
                label="Win rate"
                value={`${Math.round((community.record.winRate || 0) * 100)}%`}
              />
            </Box>
          ) : (
            <Invite copy="Win a roster battle." />
          )}
          {showcase && (
            <Box
              component={RouterLink}
              to={showcase.href}
              sx={{
                display: "block",
                mt: 1.5,
                p: 1.5,
                borderRadius: 2,
                textDecoration: "none",
                color: "inherit",
                border: `1px solid ${brandRgba(accent, 0.35)}`,
                background: `linear-gradient(135deg, ${brandRgba(accent, 0.12)} 0%, rgba(255,255,255,0.02) 100%)`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                <SportsKabaddi sx={{ color: accent, fontSize: 18 }} />
                <Typography variant="caption" sx={{ color: accent, fontWeight: 700 }}>
                  Showcase win
                </Typography>
              </Box>
              <Typography fontWeight={800} noWrap>
                {showcase.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                vs {showcase.opponentTitle} · {showcase.myVotes}–{showcase.theirVotes}
              </Typography>
            </Box>
          )}
        </Shelf>
      </Box>
    </Box>
  );
};

export default FanHubTrophyRoom;
